import { storageManager, sessionStorageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import { useInstagramStore } from '@/stores/instagramStore';
import { isNativePlatform, openOAuthUrl, closeInAppBrowserIfOpen } from './platformAdapter';

const INSTAGRAM_CLIENT_ID = import.meta.env.VITE_INSTAGRAM_CLIENT_ID;
const INSTAGRAM_AUTH_URL =
  import.meta.env.VITE_INSTAGRAM_AUTHENTICATION_URL || 'https://www.instagram.com/oauth/authorize';
const INSTAGRAM_SCOPE =
  import.meta.env.VITE_INSTAGRAM_SCOPE ||
  'instagram_business_basic,instagram_business_manage_insights';

// Base URL for Instagram redirects – falls back to main app URL if not provided
const INSTAGRAM_APP_URL = import.meta.env.VITE_INSTAGRAM_URL || import.meta.env.VITE_URL;
const APP_BASE_URL = INSTAGRAM_APP_URL;

export type InstagramAuthContext =
  | 'login'
  | 'signup'
  | 'connect'
  | 'photos'
  | 'videos'
  | 'onboarding'
  | 'dashboard'
  | 'profile';

export interface PendingOAuthState {
  state: string;
  context: InstagramAuthContext;
  returnPath?: string | null;
  redirectUri: string;
  startedAt: number;
}

/** Result of consuming an OAuth callback; used by the Bridge to complete the flow. */
export interface ConsumeCallbackResult {
  context: InstagramAuthContext;
  code: string;
  returnPath: string | null;
}

const PENDING_STATE_KEY = 'instagramPendingOAuthState';

function buildRedirectUri(): string {
  // Single HTTPS redirect that works for web and native (handled by DeepLinkService)
  return `${APP_BASE_URL}/instagram-bridge`;
}

function generateState(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

async function persistPendingState(pending: PendingOAuthState): Promise<void> {
  // Native: Preferences (via storageManager) for cold-start safety
  await storageManager.set(PENDING_STATE_KEY, pending);
  // Web: sessionStorage (via sessionStorageManager) for in-tab resilience
  await sessionStorageManager.set(PENDING_STATE_KEY, pending);
  await storageManager.set(STORAGE_KEYS.OAUTH_STATE, pending.state);
  await storageManager.set(STORAGE_KEYS.OAUTH_CONTEXT, pending.context);
}

async function loadPendingState(): Promise<PendingOAuthState | null> {
  // Prefer native-safe storage first
  const fromStorage = await storageManager.get<PendingOAuthState>(PENDING_STATE_KEY);
  if (fromStorage) return fromStorage;
  return sessionStorageManager.get<PendingOAuthState>(PENDING_STATE_KEY);
}

async function clearPendingState(): Promise<void> {
  await storageManager.remove(PENDING_STATE_KEY);
  await sessionStorageManager.remove(PENDING_STATE_KEY);
  await storageManager.remove(STORAGE_KEYS.OAUTH_STATE);
  await storageManager.remove(STORAGE_KEYS.OAUTH_CONTEXT);
}

export class InstagramAuthCore {
  /**
   * Start Instagram OAuth for the given context.
   * This only handles the browser hop and state persistence; per-context
   * behavior (SSO vs. connect vs. media import) happens in handleCallback.
   */
  static async startOAuth(
    context: InstagramAuthContext,
    options?: { returnPath?: string },
  ): Promise<void> {
    if (!INSTAGRAM_CLIENT_ID || !APP_BASE_URL) {
      throw new Error('Instagram OAuth is not configured correctly.');
    }

    const store = useInstagramStore.getState();
    const redirectUri = buildRedirectUri();
    const state = generateState();

    const pending: PendingOAuthState = {
      state,
      context,
      returnPath: options?.returnPath ?? null,
      redirectUri,
      startedAt: Date.now(),
    };

    await persistPendingState(pending);
    await store.setOAuthState(state, context as any);
    store.setLoading(true);
    store.setError(null);

    const authUrl = new URL(INSTAGRAM_AUTH_URL);
    authUrl.searchParams.set('client_id', INSTAGRAM_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('scope', INSTAGRAM_SCOPE);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('state', state);

    let finalUrl = authUrl.toString();

    // iOS/Android: prevent Instagram native app from hijacking the OAuth flow
    if (isNativePlatform()) {
      if (!finalUrl.includes('#weblink')) {
        finalUrl = `${finalUrl}#weblink`;
      }
    }

    await openOAuthUrl(finalUrl);
  }

  /**
   * Consume OAuth callback: validate state, clear pending state, close in-app browser,
   * and return context/code/returnPath for the Bridge to complete the flow.
   * Call this from the Bridge component only; do not call handleCallback from DeepLinkService.
   * @throws if no pending state or state mismatch
   */
  static async consumeCallback(
    code: string,
    stateFromUrl: string,
  ): Promise<ConsumeCallbackResult> {
    const pending = await loadPendingState();
    if (!pending) {
      throw new Error('No pending Instagram OAuth state found.');
    }
    if (pending.state !== stateFromUrl) {
      throw new Error('Invalid Instagram OAuth state.');
    }
    await clearPendingState();
    await closeInAppBrowserIfOpen();
    const store = useInstagramStore.getState();
    store.setLoading(false);
    store.setError(null);
    return {
      context: pending.context,
      code,
      returnPath: pending.returnPath ?? null,
    };
  }

  /**
   * Handle OAuth callback (legacy; prefer Bridge + consumeCallback).
   * For login/signup the Bridge now handles SSO; this is kept for non-SSO flows that may still use it.
   */
  static async handleCallback(code: string, stateFromUrl: string): Promise<void> {
    const store = useInstagramStore.getState();
    try {
      const result = await this.consumeCallback(code, stateFromUrl);
      switch (result.context) {
        case 'connect':
        case 'photos':
        case 'videos':
        case 'onboarding':
        case 'dashboard':
        case 'profile': {
          if (typeof window !== 'undefined' && result.returnPath) {
            window.location.href = result.returnPath;
          }
          break;
        }
        case 'login':
        case 'signup':
          // Bridge handles SSO; nothing to do here when Bridge is used.
          break;
        default:
          throw new Error(`Unsupported Instagram OAuth context: ${result.context}`);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Instagram connection failed.';
      console.error('InstagramAuthCore.handleCallback error:', error);
      store.setError(message);
      throw error;
    }
  }
}

